import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { http, formatApiErrorDetail } from "@/lib/api";
import ScoringGrid from "@/components/ScoringGrid";
import { STATUS_META, TIER_META, SCORING_CRITERIA } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Trash2, ExternalLink, ShieldCheck, ShieldAlert, ArrowLeft, History } from "lucide-react";

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [ref, setRef] = useState(null);
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);
  const [editLog, setEditLog] = useState([]);

  const load = () => {
    http.get(`/leads/${id}`).then((r) => setLead(r.data));
    http.get(`/leads/${id}/edit-log`).then((r) => setEditLog(r.data));
  };

  useEffect(() => {
    http.get("/reference").then((r) => setRef(r.data));
    load(); /* eslint-disable-next-line */
  }, [id]);

  const merged = useMemo(() => ({ ...(lead || {}), ...dirty }), [lead, dirty]);

  const update = (k, v) => setDirty((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    setBusy(true);
    try {
      const { data } = await http.patch(`/leads/${id}`, dirty);
      setLead(data);
      setDirty({});
      toast.success("Lead saved");
      http.get(`/leads/${id}/edit-log`).then((r) => setEditLog(r.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doQualify = async () => {
    setBusy(true);
    try {
      const payload = { ...dirty, status: "Qualified" };
      const { data } = await http.patch(`/leads/${id}`, payload);
      setLead(data); setDirty({});
      toast.success("Lead qualified");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cannot qualify");
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    const reason = window.prompt("Rejection reason:");
    if (!reason) return;
    try {
      const { data } = await http.patch(`/leads/${id}`, { status: "Rejected", rejection_reason: reason });
      setLead(data); setDirty({});
      toast.success("Lead rejected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!lead || !ref) return <Layout title="Lead"><div className="text-slate-500">Loading…</div></Layout>;

  const st = STATUS_META[merged.status] || STATUS_META["New"];
  const tm = TIER_META[merged.tier];
  const score = merged.weighted_score;
  const filled = SCORING_CRITERIA.filter((c) => c.weight > 0 && merged[c.key] != null).length;

  const sourceInput = (label, valueKey, sourceKey, verifiedKey, testidPrefix) => {
    const val = merged[valueKey] || "";
    const src = merged[sourceKey] || "";
    const verified = merged[verifiedKey];
    return (
      <div className="space-y-2 p-3 rounded-md bg-slate-50 border border-slate-200">
        <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">{label}</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input value={val} onChange={(e) => update(valueKey, e.target.value)}
                 data-testid={`${testidPrefix}-value`} placeholder={label.toLowerCase()} />
          <Input value={src} onChange={(e) => update(sourceKey, e.target.value)}
                 data-testid={`${testidPrefix}-source`} placeholder="Source URL (required)" />
        </div>
        {verifiedKey && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={!!verified} onCheckedChange={(v) => update(verifiedKey, !!v)}
                      data-testid={`${testidPrefix}-verified`} />
            <span className="flex items-center gap-1.5">
              {verified ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> : <ShieldAlert className="w-3.5 h-3.5 text-slate-400" />}
              Verified
            </span>
          </label>
        )}
      </div>
    );
  };

  return (
    <Layout
      title={merged.company_name}
      subtitle={`${merged.country} · ${merged.industry}`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => nav("/leads")} data-testid="btn-back-leads">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button onClick={save} disabled={busy || Object.keys(dirty).length === 0}
                  data-testid="btn-save-lead"
                  className="bg-amber-600 hover:bg-amber-700 text-white">
            <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : `Save${Object.keys(dirty).length ? ` (${Object.keys(dirty).length})` : ""}`}
          </Button>
        </div>
      }
    >
      {/* Header status/score card */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${st.className}`} data-testid="lead-status-badge">
            {merged.status}
          </span>
          {tm && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${tm.className}`} data-testid="lead-tier-badge">
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${tm.dot}`}></span>{tm.label}
            </span>
          )}
          <div className="text-xs text-slate-500 ml-auto">
            Added by {lead.added_by_name} · {new Date(lead.date_added).toLocaleString()}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Weighted score</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="font-heading font-bold text-5xl text-slate-900 tracking-tight" data-testid="weighted-score">
                {score != null ? score.toFixed(1) : "—"}
              </div>
              <div className="text-sm text-slate-500">/ 100</div>
              {merged.recommended_action && (
                <div className="ml-auto text-right text-sm text-slate-700 italic max-w-md">{merged.recommended_action}</div>
              )}
            </div>
            <Progress value={score || 0} className="h-2 mt-3" />
            <div className="text-xs text-slate-500 mt-2">
              {filled}/11 scoring fields filled · {merged.score_status || "Not yet scored"}
            </div>
          </div>

          <div className="border-l border-slate-100 pl-6">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Actions</div>
            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={doQualify} data-testid="btn-qualify" disabled={busy}>Attempt Qualify (V3)</Button>
              <Button variant="outline" onClick={doReject} data-testid="btn-reject" disabled={busy}
                      className="text-rose-700 hover:bg-rose-50 border-rose-200">Reject Lead</Button>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="identity" className="w-full">
        <TabsList data-testid="lead-tabs">
          <TabsTrigger value="identity" data-testid="tab-identity">Identity</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="scoring"  data-testid="tab-scoring">Scoring</TabsTrigger>
          <TabsTrigger value="audit"    data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* IDENTITY */}
        <TabsContent value="identity" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Company name</Label>
                <Input value={merged.company_name || ""} onChange={(e) => update("company_name", e.target.value)}
                       data-testid="input-company" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Website</Label>
                <Input value={merged.website || ""} onChange={(e) => update("website", e.target.value)}
                       data-testid="input-website" className="mt-1.5" placeholder="https://…" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Country</Label>
                <Select value={merged.country} onValueChange={(v) => update("country", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="select-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ref.all_countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">City</Label>
                <Input value={merged.city || ""} onChange={(e) => update("city", e.target.value)}
                       data-testid="input-city" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Industry</Label>
                <Select value={merged.industry} onValueChange={(v) => update("industry", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="select-industry"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ref.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Operating status</Label>
                <Select value={merged.status_operating || ""} onValueChange={(v) => update("status_operating", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="select-operating"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {ref.status_operating.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Est. company size</Label>
                <Input value={merged.est_company_size || ""} onChange={(e) => update("est_company_size", e.target.value)}
                       data-testid="input-size" className="mt-1.5" placeholder="e.g. 100-250 employees" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Est. pipeline value (USD)</Label>
                <Input type="number" value={merged.est_pipeline_value ?? ""}
                       onChange={(e) => update("est_pipeline_value", e.target.value === "" ? null : parseFloat(e.target.value))}
                       data-testid="input-pipeline" className="mt-1.5" placeholder="0" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Original source *</Label>
              <Input value={merged.source || ""} onChange={(e) => update("source", e.target.value)}
                     data-testid="input-source" className="mt-1.5" />
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">One-line reason</Label>
              <Textarea value={merged.one_line_reason || ""} onChange={(e) => update("one_line_reason", e.target.value)}
                        data-testid="input-reason" className="mt-1.5" rows={2} maxLength={200} />
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider mb-2 block">Products needed (required for Qualified)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ref.products.map((p) => {
                  const checked = (merged.products_needed || []).includes(p);
                  return (
                    <label key={p} className="flex items-center gap-2 p-2 border border-slate-200 rounded-md text-sm hover:bg-slate-50 cursor-pointer" data-testid={`product-${p}`}>
                      <Checkbox checked={checked} onCheckedChange={(v) => {
                        const cur = merged.products_needed || [];
                        update("products_needed", v ? [...cur, p] : cur.filter((x) => x !== p));
                      }} />
                      <span>{p}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {merged.rejection_reason && (
              <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-sm">
                <span className="font-semibold text-rose-800">Rejection reason: </span>
                <span className="text-rose-700">{merged.rejection_reason}</span>
              </div>
            )}
          </div>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
            <div className="text-sm text-slate-600 p-3 rounded-md bg-amber-50 border border-amber-200">
              <strong>V1 & V2:</strong> Any factual claim (email, phone, decision maker) must include a source URL before it can be saved. Verification requires a source.
            </div>

            <div>
              <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">LinkedIn</Label>
              <Input value={merged.linkedin_url || ""} onChange={(e) => update("linkedin_url", e.target.value)}
                     data-testid="input-linkedin" className="mt-1.5" placeholder="https://linkedin.com/company/…" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2 p-3 rounded-md bg-slate-50 border border-slate-200">
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Decision maker</Label>
                <Input value={merged.decision_maker_name || ""} onChange={(e) => update("decision_maker_name", e.target.value)}
                       data-testid="input-dm-name" placeholder="Name" />
                <Input value={merged.decision_maker_title || ""} onChange={(e) => update("decision_maker_title", e.target.value)}
                       data-testid="input-dm-title" placeholder="Title" />
                <Input value={merged.decision_maker_source || ""} onChange={(e) => update("decision_maker_source", e.target.value)}
                       data-testid="input-dm-source" placeholder="Source URL (required)" />
              </div>
              {sourceInput("Email", "email", "email_source", "email_verified", "input-email")}
              {sourceInput("Phone", "phone", "phone_source", "phone_verified", "input-phone")}
            </div>
          </div>
        </TabsContent>

        {/* SCORING */}
        <TabsContent value="scoring" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="text-sm text-slate-600 p-3 rounded-md bg-slate-50 border border-slate-200 mb-4">
              Fill any 8+ criteria to trigger automatic scoring. Import Dependency, Logistics Ease, and Market Growth auto-fill from the Country × Industry matrix — override with a justification if this company is an exception.
            </div>
            {Object.keys(dirty).some((k) => ["import_dependency_score","logistics_ease_score","market_growth_score"].includes(k)) && (
              <div className="mb-4">
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">Override justification (required)</Label>
                <Textarea value={merged.override_justification || ""}
                          onChange={(e) => update("override_justification", e.target.value)}
                          data-testid="input-override-justification"
                          className="mt-1.5" rows={2} placeholder="Why is this company an exception to the country-industry default?" />
              </div>
            )}
            <ScoringGrid lead={merged} onFieldChange={update} />
          </div>
        </TabsContent>

        {/* AUDIT */}
        <TabsContent value="audit" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <div className="font-heading font-semibold text-slate-900">Edit history</div>
              <span className="text-xs text-slate-500 ml-auto">{editLog.length} entries</span>
            </div>
            {editLog.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-10" data-testid="audit-empty">No edits yet.</div>
            ) : (
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="text-left px-4 py-2 font-semibold">When</th>
                    <th className="text-left px-4 py-2 font-semibold">Who</th>
                    <th className="text-left px-4 py-2 font-semibold">Field</th>
                    <th className="text-left px-4 py-2 font-semibold">Old → New</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {editLog.map((e, i) => (
                    <tr key={i} className="text-sm">
                      <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-2 text-slate-700">{e.edited_by_name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-800">{e.field_changed}</td>
                      <td className="px-4 py-2 text-slate-600">
                        <span className="line-through text-slate-400">{JSON.stringify(e.old_value)}</span>
                        {" → "}
                        <span className="font-semibold text-slate-900">{JSON.stringify(e.new_value)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
